import React from 'react';
import { Card } from 'primereact/card';

/* ─── ManageNetworkCard (section-internal sub-component) ─────────────
   Stat-list Card matching the reference's left-sidebar pattern. One Card
   with a title + a list of clickable stat rows (label on the left,
   primary-tinted value on the right). Each row is keyboard-accessible
   when `s.onClick` is provided. */
const ManageNetworkCard = ({ stats = [] }) => (
    <Card
        className="shadow-1 mb-3"
        pt={{
            body:    { className: 'p-0' },
            content: { className: 'p-0' },
        }}
    >
        <div className="px-3 py-3 border-bottom-1 surface-border">
            <h3 className="text-900 font-semibold text-base m-0">Manage Network</h3>
        </div>
        <ul className="list-none p-0 m-0">
            {stats.map((s, idx) => {
                const clickable = typeof s.onClick === 'function';
                const lastBorder = idx < stats.length - 1 ? 'border-bottom-1 surface-border' : '';
                const interactive = clickable ? 'cursor-pointer hover:surface-50' : '';
                return (
                    <li
                        key={s.label}
                        className={`flex justify-content-between align-items-center px-3 py-3 ${lastBorder} ${interactive}`}
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        onClick={clickable ? s.onClick : undefined}
                        onKeyDown={clickable ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                s.onClick();
                            }
                        } : undefined}
                    >
                        <span className="text-700 text-sm">{s.label}</span>
                        <span className="text-primary font-bold text-lg">{s.value ?? 0}</span>
                    </li>
                );
            })}
        </ul>
    </Card>
);

/**
 * ManageNetworkSidebar — left-rail widget on desktop (lg+) that holds the
 * Manage Network stat-list. Hidden on mobile (col-12 lg:col-4 hidden lg:block).
 *
 * Sticky positioning is dynamically measured by the parent — `top` =
 * topbar height + search bar height + small gap, so the sidebar anchors
 * right below the sticky search bar on ANY chrome configuration. Native
 * CSS sticky stops at the parent's bottom edge — no JS needed for that.
 * `align-self: flex-start` is required for sticky to work inside flex
 * children.
 *
 * Props:
 *   topPx — number of pixels for the sticky `top` offset
 *   stats — array of `{ label, value, onClick? }` rows for the list
 */
const ManageNetworkSidebar = ({ topPx, stats }) => (
    <div
        className="col-12 lg:col-4 hidden lg:block"
        style={{
            position: 'sticky',
            top: `${topPx}px`,
            alignSelf: 'flex-start',
        }}
    >
        <ManageNetworkCard stats={stats} />
    </div>
);

export default React.memo(ManageNetworkSidebar);
